
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Loader, Users, Info } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { User, ChatMessage, Conversation } from './types';
import { subscribeToMessages, sendMessage, markConversationRead } from './services/chatService';
import { getUserPublicProfile, UserPublicProfile } from './services/userService';

export const ChatThreadView = ({
  user,
  authReady,
  conversationId,
  otherUserId,
  onBack,
  onShowGroupInfo,
  t
}: {
  user: User;
  authReady: boolean;
  conversationId: string;
  otherUserId: string;
  onBack: () => void;
  onShowGroupInfo?: () => void;
  t?: any;
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [otherUser, setOtherUser] = useState<UserPublicProfile | null>(null);
  const [senderProfiles, setSenderProfiles] = useState<Record<string, UserPublicProfile>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Load conversation data
  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user is authenticated BEFORE creating listener
    const authUid = auth.currentUser?.uid;
    if (!authUid) {
      if (import.meta.env.DEV) {
        console.warn('[CHAT_THREAD] onSnapshot blocked: no authenticated user');
      }
      setLoading(false);
      return;
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(
        conversationRef,
        (snap) => {
          if (snap.exists()) {
            const data = { id: snap.id, ...snap.data() } as Conversation;
            setConversation(data);
            
            // Load other user profile for direct/private chats
            if (data.type === 'direct' || data.type === 'private') {
              getUserPublicProfile(otherUserId).then(setOtherUser);
            }
          }
        },
        (error) => {
          // CRITICAL: Handle permission-denied errors gracefully - don't crash the UI
          if (error.code === 'permission-denied') {
            if (import.meta.env.DEV) {
              console.warn('[CHAT] Permission denied for conversation - user may not have access');
            }
            // Don't update state - conversation remains null, UI handles gracefully
          } else {
            if (import.meta.env.DEV) {
              console.error('[CHAT] Error loading conversation:', error);
            }
          }
        }
      );
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[CHAT] Error initializing conversation snapshot listener:', initError);
      }
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, conversationId, otherUserId]);

  // Subscribe to messages
  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // CRITICAL: Initialize as null, assign only if subscribeToMessages returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const unsubscribeResult = subscribeToMessages(conversationId, (msgs) => {
        setMessages(msgs);
        setLoading(false);

        // Load sender profiles for group messages
        if (conversation?.type === 'group') {
          const uniqueSenders = Array.from(new Set(msgs.map(m => m.senderId)));
          uniqueSenders.forEach(senderId => {
            if (!senderProfiles[senderId]) {
              getUserPublicProfile(senderId).then(profile => {
                setSenderProfiles(prev => ({ ...prev, [senderId]: profile }));
              });
            }
          });
        }
      });
      if (typeof unsubscribeResult === 'function') {
        unsubscribe = unsubscribeResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[CHAT] Error initializing messages subscription:', initError);
      }
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [conversationId, conversation?.type]);

  // Mark conversation as read when component mounts
  useEffect(() => {
    markConversationRead(conversationId, user.id).catch(console.error);
  }, [conversationId, user.id]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    // Prevent double-submit
    if (!messageText.trim() || sending || !conversationId) return;

    setSending(true);
    try {
      await sendMessage(conversationId, user.id, messageText.trim());
      setMessageText('');
    } catch (error) {
      console.error('[CHAT] Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(t?.chat_error_send || `Error sending message: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessageTime = (timestamp: any): string => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader className="animate-spin text-cyan-500 w-12 h-12" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center gap-4 p-4 md:p-6">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          {conversation?.type === 'group' ? (
            <>
              <div className="flex items-center gap-3 flex-1">
                {conversation.photoURL ? (
                  <img
                    src={conversation.photoURL}
                    alt={conversation.title}
                    className="w-10 h-10 rounded-xl object-cover border-2 border-slate-800"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                    <Users className="text-slate-500" size={20} />
                  </div>
                )}
                <div>
                  <h2 className="font-black text-white text-sm">{conversation.title || (t?.chat_group_default_name || 'Group')}</h2>
                  <p className="text-[10px] text-slate-500">{conversation.participants.length} {t?.chat_group_members?.toLowerCase() || 'members'}</p>
                </div>
              </div>
              {onShowGroupInfo && (
                <button
                  onClick={onShowGroupInfo}
                  className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
                  title="Info gruppo"
                >
                  <Info size={20} />
                </button>
              )}
            </>
          ) : (
            otherUser && (
              <div className="flex items-center gap-3 flex-1">
                <img
                  src={otherUser.photoURL}
                  alt={otherUser.displayName}
                  className="w-10 h-10 rounded-xl object-cover border-2 border-slate-800"
                />
                <div>
                  <h2 className="font-black text-white text-sm">{otherUser.displayName}</h2>
                  {otherUser.username && (
                    <p className="text-[10px] text-slate-500">@{otherUser.username}</p>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Messages List */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-slate-500 text-sm">{t?.chat_no_messages || 'No messages yet'}</p>
            <p className="text-slate-600 text-xs mt-2">{t?.chat_start_conversation || 'Start the conversation'}</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === user.id;
            const isGroup = conversation?.type === 'group';
            const senderProfile = isGroup && !isOwn && conversation?.type !== 'private' && conversation?.type !== 'direct'
              ? senderProfiles[message.senderId] 
              : null;
            
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${isGroup ? 'items-start' : 'items-end'} gap-2`}
              >
                {isGroup && !isOwn && senderProfile && (
                  <img
                    src={senderProfile.photoURL}
                    alt={senderProfile.displayName}
                    className="w-8 h-8 rounded-xl object-cover border border-slate-800 flex-shrink-0"
                  />
                )}
                <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[75%] md:max-w-[60%]`}>
                  {isGroup && !isOwn && senderProfile && (
                    <p className="text-[10px] text-slate-400 font-bold mb-1 px-1">
                      {senderProfile.displayName}
                    </p>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-3 ${
                      isOwn
                        ? 'bg-cyan-500 text-white'
                        : 'bg-slate-800 text-slate-200'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {message.text}
                    </p>
                    <p
                      className={`text-[10px] mt-1 ${
                        isOwn ? 'text-cyan-100' : 'text-slate-500'
                      }`}
                    >
                      {formatMessageTime(message.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 bg-[#0a0a0f]/95 backdrop-blur-xl border-t border-slate-800 p-4">
        <form onSubmit={handleSend} className="flex items-end gap-3">
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t?.chat_message_placeholder || "Write a message..."}
            className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-cyan-500 transition-colors"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
            }}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className="p-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-xl transition-all disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <Loader className="animate-spin" size={20} />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

